import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import { ArrowBack } from "@styled-icons/material";

import Stack from "../../../components/Stack";
import { mediaQueries, mediaQueriesUntil } from "../../../styles/media.js";
import "../../../probate/theme.css";

const Layout = styled.div`
  display: grid;
  font-family: var(--font-sans);
  font-weight: var(--juno-font-weight-regular);
  color: var(--foreground);
  gap: var(--juno-space-3);
  align-items: start;

  ${mediaQueriesUntil.md} {
    padding: var(--juno-space-4) var(--juno-space-2);
  }

  /* The client dashboard design's 640px centre column */
  ${mediaQueries.xl} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 40rem) minmax(0, 1fr);
    column-gap: var(--juno-space-12);
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  min-height: var(--button-height, var(--juno-space-12));
  align-items: center;
  gap: var(--juno-space-2);
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);

  /* && outranks GlobalStyle's link colours and hover underline */
  &&:link,
  &&:visited,
  &&:hover,
  &&:active,
  &&:focus-visible {
    border-bottom: 0;
    color: var(--foreground);
  }

  svg {
    width: var(--juno-space-4);
  }
`;

// Below xl a request page's side column is its back link alone, above the sheet in the main
// column's width
const Aside = styled.aside`
  padding: 0 var(--juno-space-2);

  ${mediaQueriesUntil.xl} {
    justify-self: center;
    width: 100%;
    max-width: 40rem;
    text-align: center;

    &[data-case-page="false"] {
      /* With Layout's 12px, 16px above the sheet */
      margin-bottom: var(--juno-space-1);
      padding: 0 var(--juno-space-4);
      text-align: left;

      > p {
        display: none;
      }
    }
  }

  ${mediaQueries.xl} {
    position: sticky;
    top: var(--juno-space-6);
  }
`;

const AsideText = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);
  font-weight: var(--juno-font-weight-regular);
`;

// Stack zeroes legacy cards' bottom margins and spaces them by the card padding. The design's
// cards sit inside the column, 16px on a phone and 24px on a desktop; ProbateStatus widens to fill it.
const Main = styled(Stack)`
  --probate-card-inset: var(--juno-space-4);
  min-width: 0;
  padding: 0 var(--probate-card-inset);

  /* The phone layout, at most the 640px column wide, until the desktop's three columns */
  ${mediaQueriesUntil.xl} {
    justify-self: center;
    width: 100%;
    max-width: 40rem;
  }

  ${mediaQueries.xl} {
    --probate-card-inset: var(--juno-space-6);
  }
`;

// The case page's three columns. "Probate" heads the case page; on every other page it sits
// under the back link, level with the sheet's top.
const ProbateLayout = ({
  caseReference,
  casePage = false,
  back = { to: `/dashboard/${caseReference}`, label: "Dashboard" },
  index,
  children,
}) => (
  <Layout>
    <Aside data-case-page={casePage}>
      {!casePage && (
        <BackLink to={back.to} state={back.state}>
          <ArrowBack aria-hidden="true" />
          {back.label}
        </BackLink>
      )}
      <AsideText as={casePage ? "h1" : "p"}>Probate</AsideText>
      <AsideText>Case {caseReference}</AsideText>
    </Aside>
    <Main gap="lg">{children}</Main>
    {index ?? <div />}
  </Layout>
);

export default ProbateLayout;
